<template>
  <div class="pipelines-page">
    <section class="pipelines-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Automation</span>
          <h4>Pipelines</h4>
        </div>
        <div class="pipeline-head-actions">
          <span class="live-connection" :class="{ online: liveConnected }">
            <i></i>{{ liveConnected ? 'Live' : 'Reconnecting...' }}
          </span>
          <button class="btn-refresh" :disabled="loading" @click="refreshPipelines()">
            {{ loading ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>
      </div>
      <p class="intro">
        Compose scheduled jobs into ordered workflows, run them on demand, and inspect the
        exact catalog fields changed by each pipeline run.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <section v-if="!loading && !pipelines.length" class="empty-panel">
      <span class="icon icon-cycle"></span>
      <strong>No pipelines returned.</strong>
      <button class="btn-run" @click="fetchAll(true)">Reload seeded pipelines</button>
    </section>

    <section v-else class="pipeline-workspace">
      <section class="pipeline-index-panel">
        <div class="section-title-row">
          <div>
            <span class="section-title">Pipeline list</span>
            <p>Select a pipeline to edit steps, schedule, and run history.</p>
          </div>
        </div>

        <AdminTable
          class="pipeline-index-table"
          :columns="pipelineColumns"
          :rows="pipelines"
          :row-key="'key'"
          :row-class="pipelineRowClass"
          :loading="loading"
          clickable
          min-width="960px"
          empty-text="No pipelines found."
          @row-click="selectPipelineRow"
        >
          <template v-slot:cell-name="{ row }">
            <div class="pipeline-name-cell">
              <strong>{{ row.name }}</strong>
              <small>{{ row.key }}</small>
              <span v-if="row.stepIssues && row.stepIssues.length" class="missing-chip">
                {{ row.stepIssues.length }} missing job{{ row.stepIssues.length === 1 ? '' : 's' }}
              </span>
            </div>
          </template>
          <template v-slot:cell-enabled="{ row }">
            <label class="mini-switch" @click.stop>
              <input type="checkbox" :checked="row.enabled" @change="quickTogglePipeline(row, $event)">
              <span class="switch-track"></span>
              <span>{{ row.enabled ? 'Enabled' : 'Off' }}</span>
            </label>
          </template>
          <template v-slot:cell-status="{ row }">
            <span class="status-pill" :class="'st-' + pipelineStatus(row)">
              {{ statusLabel(pipelineStatus(row)) }}
            </span>
          </template>
          <template v-slot:cell-schedule="{ row }">
            <span>{{ row.enabled ? cronHint(row.cron) : 'Manual only' }}</span>
          </template>
          <template v-slot:cell-steps="{ row }">
            {{ stepCount(row) }}
          </template>
          <template v-slot:cell-lastRun="{ row }">
            <span v-if="row.lastRunAt" :title="fmtTime(row.lastRunAt)">{{ fmtRelative(row.lastRunAt) }}</span>
            <span v-else class="muted-sub">Never</span>
          </template>
          <template v-slot:cell-actions="{ row }">
            <div class="table-actions">
              <button class="btn-ghost compact" @click.stop="selectPipeline(row.key)">Edit</button>
              <button class="btn-run compact" :disabled="pipelineIsRunning(row) || !stepCount(row)" @click.stop="runPipeline(row)">
                {{ pipelineIsRunning(row) ? 'Running...' : 'Run' }}
              </button>
            </div>
          </template>
        </AdminTable>
      </section>

      <div v-if="draft" class="pipeline-editor-modal" @click.self="closeEditor()">
        <main class="pipeline-main">
        <section class="editor-panel editor-header-panel">
          <div class="editor-head">
            <div class="editor-title-block">
              <span class="eyebrow">Pipeline editor</span>
              <h5>{{ draft.name || 'Untitled pipeline' }}</h5>
              <p>{{ selectedPipeline && selectedPipeline.isSystem ? 'System pipeline. Disable it instead of deleting unless you want it recreated later.' : 'Custom pipeline.' }}</p>
              <div class="editor-summary-row">
                <span>{{ draft.steps.length }} step{{ draft.steps.length === 1 ? '' : 's' }}</span>
                <span>{{ draft.enabled ? cronHint(draft.cron) : 'Manual only' }}</span>
                <span>{{ draft.onError === 'stop' ? 'Stops on error' : 'Continues on error' }}</span>
              </div>
            </div>
            <div class="editor-actions">
              <span v-if="dirty" class="dirty-pill">Unsaved changes</span>
              <button class="btn-ghost" :disabled="!dirty || saving" @click="resetDraft()">Reset</button>
              <button class="btn-save" :disabled="!dirty || saving || !canSaveDraft" @click="saveDraft()">
                {{ saving ? 'Saving...' : 'Save changes' }}
              </button>
              <button class="btn-danger-subtle" :disabled="deleting" @click="deletePipeline()">
                Delete
              </button>
              <button class="close-btn" @click="closeEditor()">x</button>
            </div>
          </div>

          <div v-if="!canSaveDraft" class="warning-row danger">
            A scheduled pipeline must have at least one enabled step.
          </div>
        </section>

        <section class="editor-panel editor-settings-panel">
          <div class="settings-section-head">
            <span class="section-title">Basics and schedule</span>
            <p>Name the workflow, choose when it can run, and set the default failure behavior.</p>
          </div>

          <div class="pipeline-settings">
            <label class="field field-wide">
              <span>Name</span>
              <input v-model="draft.name" type="text">
            </label>
            <label class="field">
              <span>Key</span>
              <input :value="draft.key" type="text" disabled>
            </label>
            <label class="field field-wide">
              <span>Description</span>
              <textarea class="description-input" v-model="draft.description" rows="4"></textarea>
            </label>
            <label class="field">
              <span>Default error mode</span>
              <select v-model="draft.onError">
                <option value="stop">Stop on error</option>
                <option value="continue">Continue on error</option>
              </select>
            </label>
            <label class="field cron-field">
              <span>Cron</span>
              <input v-model="draft.cron" type="text" placeholder="manual only">
            </label>
            <label class="setting-switch">
              <input type="checkbox" v-model="draft.enabled">
              <span class="switch-track"></span>
              <span>{{ draft.enabled ? 'Enabled' : 'Manual only' }}</span>
            </label>
          </div>

          <div class="preset-row">
            <span>Presets</span>
            <button type="button" class="preset-btn" @click="setCron(null)">Manual only</button>
            <button type="button" class="preset-btn" @click="setCron('0 3 * * *')">Daily 03:00</button>
            <button type="button" class="preset-btn" @click="setCron('0 7 * * *')">Daily 07:00</button>
            <button type="button" class="preset-btn" @click="setCron('0 4 * * 1')">Weekly Mon 04:00</button>
          </div>

          <section class="config-panel">
            <div class="section-title-row">
              <div>
                <span class="section-title">Pipeline config</span>
                <p>Defaults merged into every run before step-level overrides.</p>
              </div>
              <button class="btn-ghost tiny" @click="addConfigRow()">Add key</button>
            </div>
            <div v-if="configRows.length" class="config-grid">
              <div v-for="(row, index) in configRows" :key="row.uid" class="config-row">
                <input v-model="row.key" placeholder="key" @input="syncConfigRows()">
                <input v-model="row.value" placeholder="value" @input="syncConfigRows()">
                <button class="icon-btn danger" title="Remove config key" @click="removeConfigRow(index)">
                  <span class="icon icon-cross"></span>
                </button>
              </div>
            </div>
            <span v-else class="muted-line">No pipeline-wide config.</span>
          </section>
        </section>

        <div class="pipeline-workflow-column">
        <section class="steps-panel">
          <div class="section-title-row">
            <div>
              <span class="section-title">Steps</span>
              <p>Array order is execution order. Step changes save together.</p>
            </div>
            <div class="add-step">
              <select v-model="addJobKey">
                <option value="">Add step...</option>
                <optgroup v-for="group in groupedJobs" :key="group.label" :label="group.label">
                  <option v-for="job in group.jobs" :key="job.key" :value="job.key">
                    {{ job.name }}{{ job.hidden ? ' (stage)' : '' }}
                  </option>
                </optgroup>
              </select>
              <button class="btn-save compact" :disabled="!addJobKey" @click="addStep()">Add</button>
            </div>
          </div>

          <div v-if="missingJobKeys.length" class="warning-row danger">
            Unregistered jobs: {{ missingJobKeys.join(', ') }}. Remove or replace them before relying on this pipeline.
          </div>

          <div class="step-list">
            <article
              v-for="(step, index) in draft.steps"
              :key="step.localId"
              class="step-row"
              :class="{ missing: step.jobExists === false, disabled: !step.enabled }"
            >
              <div class="step-row-main">
                <span class="step-pos">{{ index + 1 }}</span>
                <div class="step-copy">
                  <div class="step-title-line">
                    <strong>{{ step.label || jobName(step.jobKey) }}</strong>
                    <span v-if="jobForStep(step).hidden" class="stage-chip">stage</span>
                    <span v-if="step.jobExists === false" class="missing-chip">missing job</span>
                    <span v-if="step.conflictsWithOwnSchedule" class="conflict-chip">own schedule</span>
                    <span v-if="optionSummary(step).length" class="option-chip">{{ optionSummary(step) }}</span>
                  </div>
                  <small><span>{{ jobName(step.jobKey) }}</span><b>{{ step.jobKey }}</b></small>
                </div>
                <div class="step-controls">
                  <label class="mini-switch">
                    <input type="checkbox" v-model="step.enabled">
                    <span class="switch-track"></span>
                    <span>{{ step.enabled ? 'On' : 'Off' }}</span>
                  </label>
                  <div class="step-actions">
                    <div class="move-actions">
                      <button class="icon-btn" :disabled="index === 0" title="Move up" @click="moveStep(index, -1)">
                        <span class="icon icon-chevron-up"></span>
                      </button>
                      <button class="icon-btn" :disabled="index === draft.steps.length - 1" title="Move down" @click="moveStep(index, 1)">
                        <span class="icon icon-chevron-down"></span>
                      </button>
                    </div>
                    <button class="btn-ghost compact step-edit-btn" @click="toggleStep(step)">
                      {{ expandedSteps[step.localId] ? 'Done' : 'Edit' }}
                    </button>
                    <button class="icon-btn danger" title="Remove step" @click="removeStep(index)">
                      <span class="icon icon-trash"></span>
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="step.conflictsWithOwnSchedule" class="warning-row">
                <span>
                  {{ jobName(step.jobKey) }} is also scheduled on its own ({{ step.conflictsWithOwnSchedule }})
                  in Scheduled Jobs. It will run twice, and a collision makes this step skip with already-running.
                </span>
                <router-link to="/users/jobs">Disable individual schedule</router-link>
              </div>

              <div v-if="expandedSteps[step.localId]" class="step-editor">
                <div class="step-editor-section step-editor-section-main">
                  <span class="step-editor-title">Step identity</span>
                <label class="field field-wide">
                  <span>Step label</span>
                  <input v-model="step.label" type="text" :placeholder="jobName(step.jobKey)">
                </label>
                <label class="field">
                  <span>Error mode</span>
                  <select v-model="step.onError">
                    <option :value="null">Inherit pipeline</option>
                    <option value="stop">Stop on error</option>
                    <option value="continue">Continue on error</option>
                  </select>
                </label>
                </div>

                <div v-if="jobOptionsForStep(step).length" class="step-editor-section">
                  <span class="step-editor-title">Run options</span>
                  <div class="option-form">
                  <div
                    v-for="opt in jobOptionsForStep(step)"
                    :key="opt.key"
                    class="opt-row"
                    :class="{ wide: opt.type === 'text' }"
                  >
                    <label>{{ optionLabel(opt) }}</label>
                    <select v-if="opt.type === 'select'" v-model="step.options[opt.key]">
                      <option v-for="choice in optionChoices(opt)" :key="choice.value" :value="choice.value">{{ choice.label }}</option>
                    </select>
                    <input v-else-if="opt.type === 'number'" type="number" v-model.number="step.options[opt.key]">
                    <label v-else-if="opt.type === 'boolean'" class="mini-switch inline">
                      <input type="checkbox" v-model="step.options[opt.key]">
                      <span class="switch-track"></span>
                      <span>{{ step.options[opt.key] ? 'On' : 'Off' }}</span>
                    </label>
                    <textarea v-else-if="opt.type === 'text'" v-model="step.options[opt.key]" rows="2"></textarea>
                    <input v-else type="text" v-model="step.options[opt.key]">
                  </div>
                  </div>
                </div>
                <div v-else class="muted-line">This job has no configurable options.</div>

                <div class="runif-panel">
                  <div class="section-title-row compact">
                    <div>
                      <span class="section-title">Conditional run</span>
                      <p>{{ runIfSummary(step) }}</p>
                    </div>
                    <button
                      v-if="runIfSource(step)"
                      class="btn-ghost tiny"
                      @click="clearRunIf(step)">Always run</button>
                    <button
                      v-else
                      class="btn-ghost tiny"
                      @click="setRunIfSource(step, 'context')">Add condition</button>
                  </div>
                  <div v-if="runIfSource(step)" class="runif-grid">
                    <label class="field">
                      <span>Source</span>
                      <select :value="runIfSource(step)" @change="setRunIfSource(step, $event.target.value)">
                        <option value="">Always run</option>
                        <option value="context">Context</option>
                        <option value="step">Prior step</option>
                      </select>
                    </label>
                    <label v-if="runIfSource(step) === 'step'" class="field">
                      <span>From step</span>
                      <select v-model.number="step.runIf.from">
                        <option v-for="n in previousStepPositions(index)" :key="n" :value="n">Step {{ n }}</option>
                      </select>
                    </label>
                    <label v-if="runIfSource(step)" class="field">
                      <span>Stat key</span>
                      <input v-model="step.runIf.key" type="text" placeholder="findings">
                    </label>
                    <label v-if="runIfSource(step)" class="field">
                      <span>Operator</span>
                      <select v-model="step.runIf.op">
                        <option value="nonEmpty">is non-empty</option>
                        <option value="gt">greater than</option>
                        <option value="eq">equals</option>
                      </select>
                    </label>
                    <label v-if="runIfSource(step) && step.runIf.op !== 'nonEmpty'" class="field">
                      <span>Value</span>
                      <input v-model="step.runIf.value" type="text" placeholder="0">
                    </label>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section v-if="selectedRuns.length" class="history-panel">
          <div class="section-title-row">
            <div>
              <span class="section-title">Run history</span>
              <p>Click a run for step status and catalog field changes.</p>
            </div>
            <button class="btn-refresh" @click="fetchSelectedDetail(false)">Refresh history</button>
          </div>

          <AdminTable
            class="pipeline-history-table"
            :columns="runColumns"
            :rows="selectedRuns"
            :row-key="'id'"
            :loading="historyLoading"
            empty-text="No runs yet."
            clickable
            min-width="900px"
            @row-click="viewRun"
          >
            <template v-slot:cell-id="{ row }">
              <strong>#{{ row.id }}</strong>
            </template>
            <template v-slot:cell-status="{ row }">
              <span class="status-pill" :class="'st-' + row.status">{{ statusLabel(row.status) }}</span>
            </template>
            <template v-slot:cell-steps="{ row }">
              {{ row.stepsSucceeded || 0 }}/{{ row.stepsTotal || 0 }}
              <span v-if="row.stepsSkipped" class="muted-sub">({{ row.stepsSkipped }} skipped)</span>
            </template>
            <template v-slot:cell-changes="{ row }">
              <span :class="{ 'muted-sub': !runChangeCount(row) }">{{ runChangeCount(row) }}</span>
            </template>
            <template v-slot:cell-duration="{ row }">
              {{ duration(row) }}
            </template>
            <template v-slot:cell-started="{ row }">
              <span :title="fmtTime(row.startedAt)">{{ fmtRelative(row.startedAt) }}</span>
            </template>
          </AdminTable>
        </section>
        </div>
        </main>
      </div>
    </section>

    <div v-if="selectedRun" class="run-drawer" @click.self="closeRun()">
      <div class="run-panel">
        <div class="panel-head">
          <div>
            <span class="eyebrow">Pipeline run</span>
            <h5>Run #{{ selectedRun.id }}</h5>
          </div>
          <div class="panel-head-actions">
            <span
              class="status-pill"
              :class="['st-' + selectedRun.status, { 'is-live-status': isActiveRun(selectedRun) }]"
            >{{ statusLabel(selectedRun.status) }}</span>
            <button v-if="isActiveRun(selectedRun)" class="btn-danger-subtle" @click="cancelPipelineRun(selectedRun)">Cancel</button>
            <button class="close-btn" @click="closeRun()">x</button>
          </div>
        </div>

        <div v-if="isActiveRun(selectedRun)" class="run-active-progress" aria-label="Pipeline is actively running">
          <span></span>
        </div>

        <div v-if="isActiveRun(selectedRun)" class="run-live-strip">
          <div class="run-live-copy">
            <span class="run-live-dot" aria-hidden="true"></span>
            <div>
              <strong>{{ activeStepTitle(selectedRun) }}</strong>
              <span>{{ runProgressLabel(selectedRun) }}</span>
            </div>
          </div>
          <div class="run-heartbeat">
            <span>Live updates</span>
            <small>Updated {{ runUpdatedLabel() }}</small>
          </div>
        </div>

        <div class="run-summary-grid">
          <div><span>Pipeline</span><strong>{{ selectedRun.pipelineKey || selectedKey }}</strong></div>
          <div><span>Trigger</span><strong>{{ selectedRun.trigger || '-' }}</strong></div>
          <div><span>Steps</span><strong>{{ runProgressLabel(selectedRun) }}</strong></div>
          <div><span>Changes</span><strong>{{ changeSummary(selectedRun) }}</strong></div>
          <div><span>Started</span><strong>{{ fmtTime(selectedRun.startedAt) }}</strong></div>
          <div><span>Duration</span><strong>{{ liveDuration(selectedRun) }}</strong></div>
        </div>

        <section class="run-section">
          <div class="section-title">Step timeline</div>
          <div class="timeline">
            <article
              v-for="step in runSteps(selectedRun)"
              :key="step.id || step.position"
              class="timeline-step"
              :class="'st-' + (step.status || 'pending')"
            >
              <span class="timeline-pos">{{ step.position }}</span>
              <div class="timeline-main">
                <div class="timeline-title">
                  <strong>{{ step.label || step.jobName || step.jobKey }}</strong>
                </div>
                <div class="timeline-meta">
                  <span><b>Job</b>{{ step.jobName || step.jobKey || '-' }}</span>
                  <span v-if="stepScopeLabel(step)"><b>Scope</b>{{ stepScopeLabel(step) }}</span>
                  <span><b>Duration</b>{{ liveDuration(step) }}</span>
                  <span><b>Changes</b>{{ stepChangeCount(step) }}</span>
                </div>
                <p v-if="step.skipReason" class="skip-reason">{{ step.skipReason }}</p>
                <p v-if="step.error" class="step-error">{{ step.error }}</p>
                <div v-if="headlineStats(step.stats).length" class="stat-row">
                  <span v-for="stat in headlineStats(step.stats)" :key="stat.key" class="stat-chip">
                    <b>{{ stat.label }}</b>{{ stat.value }}
                  </span>
                </div>
              </div>
              <div class="timeline-actions">
                <span
                  class="status-pill"
                  :class="['st-' + (step.status || 'pending'), { 'is-live-status': isExecutingStep(step) }]"
                >{{ statusLabel(step.status || 'pending') }}</span>
                <router-link
                  v-if="step.jobRunId"
                  class="job-run-link"
                  :to="{ path: '/users/jobs', query: { run: String(step.jobRunId) } }"
                  :aria-label="`View job log for ${step.label || step.jobName || step.jobKey}`"
                >View log</router-link>
              </div>
            </article>
          </div>
        </section>

        <section class="run-section">
          <div class="section-title-row">
            <div>
              <span class="section-title">What changed</span>
              <p>Catalog field audits attributed to the job run that made each change.</p>
            </div>
            <strong v-if="!isActiveRun(selectedRun) || changeTotal(selectedRun)" class="change-total">{{ changeTotal(selectedRun) }} total</strong>
            <span v-else class="changes-collecting"><i></i> Collecting results</span>
          </div>

          <div v-if="isActiveRun(selectedRun) && !changeTotal(selectedRun)" class="run-results-waiting">
            <span class="run-results-waiting__icon" aria-hidden="true">
              <span></span><span></span><span></span>
            </span>
            <div>
              <strong>Results will appear here as steps complete.</strong>
              <p>The pipeline is still working. Field changes and sample records will populate automatically.</p>
            </div>
          </div>

          <template v-else>
          <div class="change-breakdowns">
            <div class="breakdown-card">
              <span class="mini-title">By field</span>
              <div v-if="changeRows(selectedRun, 'byField').length" class="bar-list">
                <div v-for="row in changeRows(selectedRun, 'byField')" :key="row.field || row.key" class="bar-row">
                  <span>{{ row.field || row.key }}</span>
                  <b>{{ row.count }}</b>
                  <em :style="{ width: barWidth(row.count, changeRows(selectedRun, 'byField')) }"></em>
                </div>
              </div>
              <span v-else class="muted-line">No field changes.</span>
            </div>
            <div class="breakdown-card">
              <span class="mini-title">By type</span>
              <div v-if="changeRows(selectedRun, 'byType').length" class="bar-list">
                <div v-for="row in changeRows(selectedRun, 'byType')" :key="row.type || row.key" class="bar-row">
                  <span>{{ row.type || row.key }}</span>
                  <b>{{ row.count }}</b>
                  <em :style="{ width: barWidth(row.count, changeRows(selectedRun, 'byType')) }"></em>
                </div>
              </div>
              <span v-else class="muted-line">No type changes.</span>
            </div>
          </div>

          <AdminTable
            class="changes-table"
            :columns="changeColumns"
            :rows="changeSample(selectedRun)"
            :row-key="'id'"
            empty-text="No sample changes returned."
            min-width="920px"
          >
            <template v-slot:cell-part="{ row }">
              <strong>{{ row.model || '-' }}</strong>
              <small>{{ row.partType || row.type || '-' }} #{{ row.partId || '-' }} | {{ row.asin || '-' }}</small>
            </template>
            <template v-slot:cell-field="{ row }">
              <span class="field-code">{{ row.field || '-' }}</span>
            </template>
            <template v-slot:cell-before="{ row }">
              <span class="truncate-value" :title="displayValue(row.before)">{{ displayValue(row.before) }}</span>
            </template>
            <template v-slot:cell-after="{ row }">
              <span class="truncate-value" :title="displayValue(row.after)">{{ displayValue(row.after) }}</span>
            </template>
            <template v-slot:cell-source="{ row }">
              <span>{{ row.source || '-' }}</span>
              <small v-if="row.reason">{{ row.reason }}</small>
            </template>
          </AdminTable>
          </template>
        </section>

        <details class="run-log">
          <summary>
            <span>Orchestration log</span>
            <span v-if="isActiveRun(selectedRun)" class="run-log-live"><i></i> Live</span>
          </summary>
          <pre>{{ selectedRun.log || 'No orchestration log returned.' }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import realtime from '../../services/realtime'
import AdminTable from './shared/AdminTable.vue'

const ACTIVE_STATUSES = ['running', 'starting', 'queued', 'pending', 'active']

export default {
  name: 'Pipelines',
  components: {
    AdminTable
  },
  data () {
    return {
      pipelines: [],
      jobs: [],
      selectedKey: '',
      selectedPipeline: null,
      selectedRuns: [],
      selectedRun: null,
      draft: null,
      savedSnapshot: '',
      configRows: [],
      expandedSteps: {},
      addJobKey: '',
      loading: false,
      historyLoading: false,
      runLoading: false,
      saving: false,
      deleting: false,
      error: '',
      notice: '',
      unsubscribePipelinesLive: null,
      unsubscribeRunLive: null,
      unsubscribeLiveState: null,
      liveFallbackTimer: null,
      liveConnected: false,
      clockNow: Date.now(),
      runClockTimer: null,
      runLastUpdatedAt: 0,
      configUid: 0,
      localStepUid: 0
    }
  },
  computed: {
    dirty () {
      return !!this.draft && this.snapshotDraft(this.draft) !== this.savedSnapshot
    },
    canSaveDraft () {
      if (!this.draft) return false
      if (!this.draft.enabled && !this.draft.cron) return true
      return (this.draft.steps || []).some((step) => step.enabled !== false)
    },
    missingJobKeys () {
      const issues = (this.selectedPipeline && this.selectedPipeline.stepIssues) || []
      const fromSteps = (this.draft && this.draft.steps ? this.draft.steps : [])
        .filter((step) => step.jobExists === false)
        .map((step) => step.jobKey)
      return Array.from(new Set(issues.concat(fromSteps).filter(Boolean)))
    },
    groupedJobs () {
      const groups = {}
      ;(this.jobs || []).forEach((job) => {
        const label = job.categoryLabel || job.category || 'Other'
        if (!groups[label]) groups[label] = []
        groups[label].push(job)
      })
      return Object.keys(groups).sort().map((label) => ({
        label,
        jobs: groups[label].slice().sort((a, b) => String(a.name || a.key).localeCompare(String(b.name || b.key)))
      }))
    },
    pipelineColumns () {
      return [
        { key: 'name', label: 'Pipeline', width: '30%', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => r.name || r.key },
        { key: 'enabled', label: 'Mode', width: '118px', nowrap: true, sortable: true, sortAccessor: (r) => r.enabled ? 1 : 0 },
        { key: 'status', label: 'Last status', width: '122px', nowrap: true, sortable: true, sortAccessor: (r) => this.pipelineStatus(r) },
        { key: 'schedule', label: 'Schedule', width: '150px', nowrap: true },
        { key: 'steps', label: 'Steps', width: '74px', nowrap: true, sortable: true, sortAccessor: (r) => this.stepCount(r) },
        { key: 'lastRun', label: 'Last run', width: '120px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(r.lastRunAt).getTime() || 0 },
        { key: 'actions', label: '', width: '142px', align: 'right', nowrap: true }
      ]
    },
    runColumns () {
      return [
        { key: 'id', label: 'Run', width: '84px', nowrap: true, sortable: true, sortAccessor: (r) => Number(r.id) || 0 },
        { key: 'trigger', label: 'Trigger', width: '100px', nowrap: true },
        { key: 'status', label: 'Status', width: '116px', nowrap: true },
        { key: 'steps', label: 'Steps', width: '120px', nowrap: true },
        { key: 'changes', label: 'Changes', width: '100px', nowrap: true, sortable: true, sortAccessor: (r) => Number(r.changes) || 0 },
        { key: 'duration', label: 'Duration', width: '104px', nowrap: true },
        { key: 'started', label: 'Started', width: '148px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(r.startedAt).getTime() || 0 }
      ]
    },
    changeColumns () {
      return [
        { key: 'part', label: 'Part', width: '180px', breakable: true },
        { key: 'field', label: 'Field', width: '120px', nowrap: true },
        { key: 'before', label: 'Before', width: '170px', breakable: true },
        { key: 'after', label: 'After', width: '170px', breakable: true },
        { key: 'source', label: 'Source', width: '180px', breakable: true },
        { key: 'jobRunId', label: 'Job run', width: '82px', nowrap: true }
      ]
    }
  },
  watch: {
    selectedRun (run) {
      if (run) this.runLastUpdatedAt = Date.now()
    }
  },
  mounted () {
    this.fetchAll()
    this.connectPipelinesLive()
    this.runClockTimer = window.setInterval(() => {
      this.clockNow = Date.now()
    }, 1000)
    if (this.$route.query.run) {
      this.viewRun({ id: this.$route.query.run })
    }
  },
  beforeDestroy () {
    this.disconnectPipelinesLive()
    if (this.runClockTimer) window.clearInterval(this.runClockTimer)
    this.runClockTimer = null
    window.removeEventListener('beforeunload', this.beforeUnload)
  },
  created () {
    window.addEventListener('beforeunload', this.beforeUnload)
  },
  beforeRouteLeave (to, from, next) {
    if (!this.dirty || window.confirm('You have unsaved pipeline changes. Leave without saving?')) next()
    else next(false)
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    beforeUnload (event) {
      if (!this.dirty) return
      event.preventDefault()
      event.returnValue = ''
    },
    refreshPipelines () {
      this.fetchAll(true)
      realtime.refresh('pipelines:all')
      if (this.selectedRun && this.selectedRun.id) realtime.refresh(`pipelines:${this.selectedRun.id}`)
    },
    connectPipelinesLive () {
      this.liveConnected = realtime.isConnected()
      this.unsubscribeLiveState = realtime.onStateChange((state, connected, detail) => {
        this.liveConnected = !!connected
        if (connected && state === 'open') {
          if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
          this.liveFallbackTimer = null
          this.error = ''
          realtime.refresh('pipelines:all')
        } else {
          if (state === 'error') this.error = realtime.errorMessage(detail)
          this.scheduleLiveFallback(500)
        }
      })
      this.unsubscribePipelinesLive = realtime.subscribe('pipelines:all', {
        'pipeline:snapshot': this.handlePipelinesSnapshot,
        'pipeline:started': this.handlePipelineRunEvent,
        'pipeline:step': this.handlePipelineStepEvent,
        'pipeline:progress': this.handlePipelineRunEvent,
        'pipeline:finished': this.handlePipelineRunEvent,
        'pipeline:config': this.handlePipelineConfig,
        'pipeline:error': this.handlePipelineRealtimeError
      })
      this.scheduleLiveFallback(5000)
    },
    disconnectPipelinesLive () {
      if (this.unsubscribePipelinesLive) this.unsubscribePipelinesLive()
      if (this.unsubscribeRunLive) this.unsubscribeRunLive()
      if (this.unsubscribeLiveState) this.unsubscribeLiveState()
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.unsubscribePipelinesLive = null
      this.unsubscribeRunLive = null
      this.unsubscribeLiveState = null
      this.liveFallbackTimer = null
    },
    scheduleLiveFallback (delay) {
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.liveFallbackTimer = null
      if (realtime.isConnected()) return
      this.liveFallbackTimer = setTimeout(async () => {
        this.liveFallbackTimer = null
        if (realtime.isConnected()) return
        await this.fetchAll(false)
        if (this.selectedRun && this.selectedRun.id) await this.refreshSelectedRunDetail(this.selectedRun.id)
        if (!realtime.isConnected()) this.scheduleLiveFallback(5000)
      }, delay || 5000)
    },
    subscribeRunLive () {
      if (this.unsubscribeRunLive) this.unsubscribeRunLive()
      this.unsubscribeRunLive = null
      if (!this.selectedRun || !this.selectedRun.id) return
      const topic = `pipelines:${this.selectedRun.id}`
      this.unsubscribeRunLive = realtime.subscribe(topic, {
        'pipeline:snapshot': this.handleSelectedRunSnapshot,
        'pipeline:step': this.handlePipelineStepEvent,
        'pipeline:progress': this.handleSelectedRunEvent,
        'pipeline:log': this.handlePipelineLog,
        'pipeline:finished': this.handleSelectedRunEvent,
        'pipeline:error': this.handlePipelineRealtimeError
      })
      if (realtime.isConnected()) realtime.refresh(topic)
    },
    handlePipelinesSnapshot (payload) {
      const data = payload || {}
      if (Array.isArray(data.pipelines)) this.mergePipelines(data.pipelines)
      if (Array.isArray(data.runs)) this.mergePipelineRuns(data.runs)
      if (data.pipeline) this.mergePipelineInList(this.normalizePipeline(data.pipeline))
      if (data.run) this.mergePipelineRun(data.run)
    },
    handlePipelineRunEvent (payload) {
      const data = payload || {}
      if (data.pipeline) this.mergePipelineInList(this.normalizePipeline(data.pipeline))
      const run = this.runFromEvent(data)
      if (run) this.mergePipelineRun(run)
    },
    handleSelectedRunSnapshot (payload) {
      const run = this.runFromEvent(payload || {})
      if (!run) return
      this.selectedRun = this.mergeSelectedRunSnapshot(run)
      this.mergePipelineRun(run)
    },
    handleSelectedRunEvent (payload) {
      const run = this.runFromEvent(payload || {})
      if (!run) return
      this.selectedRun = this.normalizeRunDetail(Object.assign({}, this.selectedRun || {}, run))
      this.mergePipelineRun(run)
      if (this.isTerminalRun(run)) this.refreshSelectedRunDetail(run.id)
    },
    handlePipelineStepEvent (payload) {
      const data = payload || {}
      const run = this.runFromEvent(data)
      if (run) this.mergePipelineRun(run)
      const step = data.step || (data.data && data.data.step)
      if (!step) return
      const runId = data.pipelineRunId || data.pipeline_run_id || step.pipelineRunId || step.pipeline_run_id || (run && run.id)
      this.mergeStepIntoRun(runId, step)
    },
    handlePipelineLog (payload) {
      const data = payload || {}
      const runId = data.runId || data.run_id || data.pipelineRunId || data.pipeline_run_id || (data.run && data.run.id)
      const append = data.logAppend || data.log_append || data.line || data.message || ''
      if (append) this.appendPipelineLog(runId, append)
    },
    handlePipelineConfig (payload) {
      const data = payload || {}
      if (data.action === 'deleted' && data.pipelineKey) {
        this.pipelines = this.pipelines.filter((pipeline) => pipeline.key !== data.pipelineKey)
        if (this.selectedKey === data.pipelineKey) this.closeEditor()
        return
      }
      if (data.pipeline) this.mergePipelineInList(this.normalizePipeline(data.pipeline))
    },
    handlePipelineRealtimeError (payload) {
      const data = payload || {}
      this.error = data.message || data.reason || realtime.errorMessage(data)
    },
    runFromEvent (payload) {
      if (!payload || typeof payload !== 'object') return null
      return payload.run || payload.pipelineRun || payload.pipeline_run ||
        (payload.data && (payload.data.run || payload.data.pipelineRun || payload.data.pipeline_run)) || null
    },
    isTerminalRun (run) {
      const status = String((run && run.status) || '').toLowerCase()
      return ['success', 'succeeded', 'completed', 'complete', 'failed', 'error', 'cancelled', 'canceled'].includes(status)
    },
    mergeSelectedRunSnapshot (incoming) {
      const current = this.selectedRun || {}
      const next = Object.assign({}, current, incoming || {})
      const currentLog = typeof current.log === 'string' ? current.log : ''
      const incomingLog = incoming && typeof incoming.log === 'string' ? incoming.log : ''
      if (currentLog.length > incomingLog.length) next.log = currentLog
      return this.normalizeRunDetail(next)
    },
    async refreshSelectedRunDetail (id) {
      if (!id || !this.selectedRun || String(this.selectedRun.id) !== String(id)) return
      try {
        const { data } = await axios.get(`${apiDomain}pipelines/runs/${encodeURIComponent(id)}`, { headers: this.authHeaders() })
        const run = data && data.run
        if (run && this.selectedRun && String(this.selectedRun.id) === String(id)) {
          this.selectedRun = this.mergeSelectedRunSnapshot(run)
          this.mergePipelineRun(run)
        }
      } catch (error) {
        this.error = this.errMsg(error)
      }
    },
    mergePipelines (pipelines) {
      (pipelines || []).forEach((pipeline) => this.mergePipelineInList(this.normalizePipeline(pipeline)))
    },
    mergePipelineRuns (runs) {
      (runs || []).forEach((run) => this.mergePipelineRun(run))
    },
    mergePipelineRun (incoming) {
      if (!incoming || !incoming.id) return
      const run = this.normalizeRuns([incoming])[0]
      const key = run.pipelineKey || run.pipeline_key || this.selectedKey
      if (key === this.selectedKey) {
        const idx = this.selectedRuns.findIndex((item) => String(item.id) === String(run.id))
        if (idx >= 0) this.$set(this.selectedRuns, idx, Object.assign({}, this.selectedRuns[idx], run))
        else this.selectedRuns = [run].concat(this.selectedRuns || []).slice(0, 20)
      }
      this.pipelines.forEach((pipeline, index) => {
        if (pipeline.key !== key) return
        const next = Object.assign({}, pipeline, {
          lastRun: run,
          lastRunAt: run.startedAt || pipeline.lastRunAt,
          lastStatus: run.status || pipeline.lastStatus
        })
        this.$set(this.pipelines, index, next)
      })
      if (this.selectedPipeline && this.selectedPipeline.key === key) {
        this.selectedPipeline = Object.assign({}, this.selectedPipeline, {
          lastRun: run,
          lastRunAt: run.startedAt || this.selectedPipeline.lastRunAt,
          lastStatus: run.status || this.selectedPipeline.lastStatus
        })
      }
      if (this.selectedRun && String(this.selectedRun.id) === String(run.id)) {
        this.selectedRun = this.mergeSelectedRunSnapshot(run)
      }
    },
    mergeStepIntoRun (runId, step) {
      if (!runId || !step) return
      const mergeSteps = (run) => {
        const current = Array.isArray(run.steps) ? run.steps.slice() : []
        const id = step.id || step.key || step.position
        const idx = current.findIndex((item) => String(item.id || item.key || item.position) === String(id))
        if (idx >= 0) current.splice(idx, 1, Object.assign({}, current[idx], step))
        else current.push(step)
        return Object.assign({}, run, { steps: current })
      }
      const idx = this.selectedRuns.findIndex((run) => String(run.id) === String(runId))
      if (idx >= 0) this.$set(this.selectedRuns, idx, mergeSteps(this.selectedRuns[idx]))
      if (this.selectedRun && String(this.selectedRun.id) === String(runId)) {
        this.selectedRun = this.normalizeRunDetail(mergeSteps(this.selectedRun))
      }
    },
    appendPipelineLog (runId, append) {
      if (!runId || !append) return
      const idx = this.selectedRuns.findIndex((run) => String(run.id) === String(runId))
      if (idx >= 0) {
        this.$set(this.selectedRuns, idx, Object.assign({}, this.selectedRuns[idx], {
          log: `${this.selectedRuns[idx].log || ''}${append}`
        }))
      }
      if (this.selectedRun && String(this.selectedRun.id) === String(runId)) {
        this.selectedRun = this.normalizeRunDetail(Object.assign({}, this.selectedRun, {
          log: `${this.selectedRun.log || ''}${append}`
        }))
      }
    },
    async fetchAll (forceNotice) {
      this.loading = !this.pipelines.length
      this.error = ''
      try {
        const [pipelineRes, jobsRes] = await Promise.all([
          axios.get(`${apiDomain}pipelines`, { headers: this.authHeaders() }),
          axios.get(`${apiDomain}pipelines/jobs`, { headers: this.authHeaders() })
        ])
        this.pipelines = this.normalizePipelines(pipelineRes.data && pipelineRes.data.pipelines)
        this.jobs = this.normalizeJobs(jobsRes.data && jobsRes.data.jobs)
        if (!this.selectedKey && this.pipelines.length) {
          const first = this.pipelines.find((row) => row.key === this.$route.query.pipeline) || this.pipelines[0]
          this.selectedKey = first.key
        }
        if (this.selectedKey) await this.fetchSelectedDetail(false)
        if (forceNotice) this.notice = 'Pipelines refreshed.'
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    async fetchSelectedDetail (openEditor) {
      if (!this.selectedKey) return
      this.historyLoading = !this.selectedRuns.length
      try {
        const { data } = await axios.get(`${apiDomain}pipelines/${encodeURIComponent(this.selectedKey)}`, {
          headers: this.authHeaders()
        })
        const pipeline = this.normalizePipeline(data && data.pipeline)
        this.selectedPipeline = pipeline
        this.selectedRuns = this.normalizeRuns(data && data.runs)
        this.mergePipelineInList(pipeline)
        if (openEditor && !this.dirty) this.makeDraft(pipeline)
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.historyLoading = false
      }
    },
    selectPipeline (key) {
      if (this.selectedKey === key) {
        if (!this.draft) this.fetchSelectedDetail(true)
        return
      }
      if (this.dirty && !window.confirm('You have unsaved pipeline changes. Switch pipelines without saving?')) return
      this.selectedKey = key
      this.selectedRuns = []
      this.selectedRun = null
      this.$router.replace({ path: this.$route.path, query: Object.assign({}, this.$route.query, { pipeline: key }) }).catch(() => {})
      this.fetchSelectedDetail(true)
    },
    selectPipelineRow (pipeline) {
      if (pipeline && pipeline.key) this.selectPipeline(pipeline.key)
    },
    closeEditor () {
      if (this.dirty && !window.confirm('You have unsaved pipeline changes. Close without saving?')) return
      this.draft = null
      this.selectedPipeline = null
      this.selectedRuns = []
      this.expandedSteps = {}
      const query = Object.assign({}, this.$route.query)
      delete query.pipeline
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    pipelineRowClass (pipeline) {
      return {
        'is-selected': pipeline && pipeline.key === this.selectedKey,
        'is-running': this.pipelineIsRunning(pipeline),
        'has-issues': !!(pipeline && pipeline.stepIssues && pipeline.stepIssues.length)
      }
    },
    normalizePipelines (rows) {
      return (Array.isArray(rows) ? rows : []).map(this.normalizePipeline)
    },
    normalizePipeline (pipeline) {
      const row = Object.assign({
        id: null,
        key: '',
        name: '',
        description: '',
        enabled: false,
        cron: null,
        config: {},
        onError: 'continue',
        isSystem: false,
        stepIssues: [],
        steps: []
      }, pipeline || {})
      row.steps = this.normalizeSteps(row.steps || [])
      return row
    },
    normalizeJobs (rows) {
      return (Array.isArray(rows) ? rows : []).map((job) => Object.assign({
        key: '',
        name: '',
        description: '',
        category: '',
        categoryLabel: 'Other',
        hidden: false,
        options: []
      }, job || {}))
    },
    normalizeSteps (steps) {
      return (Array.isArray(steps) ? steps : []).map((step, index) => this.normalizeStep(step, index))
    },
    normalizeStep (step, index) {
      const row = Object.assign({
        id: null,
        position: index + 1,
        jobKey: '',
        label: '',
        enabled: true,
        options: {},
        onError: null,
        runIf: null,
        jobExists: true,
        conflictsWithOwnSchedule: null,
        job: null
      }, step || {})
      row.localId = row.localId || `step-${row.id || 'new'}-${++this.localStepUid}`
      row.options = Object.assign({}, row.options || {})
      row.runIf = row.runIf ? Object.assign({}, row.runIf) : null
      return row
    },
    normalizeRuns (runs) {
      return (Array.isArray(runs) ? runs : []).map((run) => {
        const row = Object.assign({}, run || {})
        row.status = String(row.status || '').toLowerCase()
        return row
      })
    },
    mergePipelineInList (pipeline) {
      if (!pipeline || !pipeline.key) return
      const idx = this.pipelines.findIndex((row) => row.key === pipeline.key)
      if (idx >= 0) this.$set(this.pipelines, idx, pipeline)
      else this.pipelines.push(pipeline)
    },
    makeDraft (pipeline) {
      if (!pipeline) return
      this.expandedSteps = {}
      this.draft = this.clonePipelineForEdit(pipeline)
      this.configRows = this.configToRows(this.draft.config)
      this.savedSnapshot = this.snapshotDraft(this.draft)
    },
    clonePipelineForEdit (pipeline) {
      const draft = JSON.parse(JSON.stringify(pipeline || {}))
      draft.enabled = !!draft.enabled
      draft.cron = draft.cron || ''
      draft.onError = draft.onError || 'continue'
      draft.config = Object.assign({}, draft.config || {})
      draft.steps = this.normalizeSteps(draft.steps || []).map((step, index) => {
        step.position = index + 1
        step.options = this.applyOptionDefaults(step)
        return step
      })
      return draft
    },
    snapshotDraft (draft) {
      if (!draft) return ''
      return JSON.stringify({
        name: draft.name || '',
        description: draft.description || '',
        enabled: !!draft.enabled,
        cron: draft.cron || '',
        config: draft.config || {},
        onError: draft.onError || 'continue',
        steps: (draft.steps || []).map((step) => ({
          id: step.id || null,
          jobKey: step.jobKey || '',
          label: step.label || '',
          enabled: step.enabled !== false,
          options: step.options || {},
          onError: step.onError || null,
          runIf: step.runIf || null
        }))
      })
    },
    configToRows (config) {
      return Object.keys(config || {}).map((key) => ({
        uid: ++this.configUid,
        key,
        value: this.configValueToInput(config[key])
      }))
    },
    syncConfigRows () {
      if (!this.draft) return
      const next = {}
      this.configRows.forEach((row) => {
        const key = String(row.key || '').trim()
        if (!key) return
        next[key] = this.inputToConfigValue(row.value)
      })
      this.$set(this.draft, 'config', next)
    },
    addConfigRow () {
      this.configRows.push({ uid: ++this.configUid, key: '', value: '' })
    },
    removeConfigRow (index) {
      this.configRows.splice(index, 1)
      this.syncConfigRows()
    },
    configValueToInput (value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    inputToConfigValue (value) {
      const text = String(value === undefined || value === null ? '' : value).trim()
      if (text === '') return ''
      if (text === 'true') return true
      if (text === 'false') return false
      if (text === 'null') return null
      const num = Number(text)
      if (text !== '' && !isNaN(num) && String(num) === text) return num
      if ((text.charAt(0) === '{' && text.charAt(text.length - 1) === '}') ||
        (text.charAt(0) === '[' && text.charAt(text.length - 1) === ']')) {
        try { return JSON.parse(text) } catch (e) { return text }
      }
      return text
    },
    setCron (cron) {
      if (!this.draft) return
      this.draft.cron = cron || ''
      if (cron) this.draft.enabled = true
    },
    resetDraft () {
      if (this.selectedPipeline) this.makeDraft(this.selectedPipeline)
    },
    async saveDraft () {
      if (!this.draft || this.saving || !this.canSaveDraft) return
      this.saving = true
      this.error = ''
      try {
        const metaBody = {
          name: this.draft.name,
          description: this.draft.description || '',
          enabled: !!this.draft.enabled,
          cron: this.draft.cron || null,
          config: this.draft.config || {},
          onError: this.draft.onError || 'continue'
        }
        const metaRes = await axios.post(`${apiDomain}pipelines/${encodeURIComponent(this.draft.key)}`, metaBody, {
          headers: this.authHeaders()
        })
        const savedMeta = this.normalizePipeline(metaRes.data && metaRes.data.pipeline)
        const stepsRes = await axios.post(`${apiDomain}pipelines/${encodeURIComponent(savedMeta.key || this.draft.key)}/steps`, {
          steps: this.draft.steps.map(this.stepPayload)
        }, { headers: this.authHeaders() })
        const saved = this.normalizePipeline(stepsRes.data && stepsRes.data.pipeline)
        this.selectedKey = saved.key
        this.selectedPipeline = saved
        this.mergePipelineInList(saved)
        this.makeDraft(saved)
        this.notice = 'Pipeline saved.'
        await this.fetchSelectedDetail(false)
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.saving = false
      }
    },
    stepPayload (step) {
      const payload = {
        jobKey: step.jobKey,
        label: step.label || '',
        enabled: step.enabled !== false,
        options: Object.assign({}, step.options || {}),
        onError: step.onError || null,
        runIf: step.runIf || null
      }
      if (step.id) payload.id = step.id
      return payload
    },
    async quickTogglePipeline (pipeline, event) {
      const enabled = !!(event && event.target && event.target.checked)
      try {
        const { data } = await axios.post(`${apiDomain}pipelines/${encodeURIComponent(pipeline.key)}`, { enabled }, {
          headers: this.authHeaders()
        })
        const saved = this.normalizePipeline(data && data.pipeline)
        this.mergePipelineInList(saved)
        if (saved.key === this.selectedKey && !this.dirty) this.makeDraft(saved)
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    async deletePipeline () {
      if (!this.selectedPipeline || this.deleting) return
      const note = this.selectedPipeline.isSystem
        ? 'This seeded pipeline may be recreated on the next backend restart. Disable it instead if you only want it to stop running.'
        : 'This deletes the pipeline definition. Existing run history remains on the backend if retained there.'
      if (!window.confirm(`${note}\n\nDelete "${this.selectedPipeline.name}"?`)) return
      this.deleting = true
      try {
        const { data } = await axios.delete(`${apiDomain}pipelines/${encodeURIComponent(this.selectedPipeline.key)}`, {
          headers: this.authHeaders()
        })
        this.notice = (data && data.note) || 'Pipeline deleted.'
        this.pipelines = this.pipelines.filter((row) => row.key !== this.selectedPipeline.key)
        this.selectedKey = this.pipelines[0] ? this.pipelines[0].key : ''
        this.selectedPipeline = null
        this.draft = null
        if (this.selectedKey) await this.fetchSelectedDetail(false)
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.deleting = false
      }
    },
    addStep () {
      const job = this.jobs.find((row) => row.key === this.addJobKey)
      if (!job || !this.draft) return
      const step = this.normalizeStep({
        jobKey: job.key,
        label: job.name,
        enabled: true,
        options: {},
        onError: null,
        runIf: null,
        jobExists: true,
        job
      }, this.draft.steps.length)
      step.options = this.applyOptionDefaults(step)
      this.draft.steps.push(step)
      this.$set(this.expandedSteps, step.localId, true)
      this.addJobKey = ''
    },
    removeStep (index) {
      const step = this.draft && this.draft.steps[index]
      if (!step) return
      if (!window.confirm(`Remove step "${step.label || step.jobKey}"?`)) return
      this.draft.steps.splice(index, 1)
      this.repositionSteps()
    },
    moveStep (index, dir) {
      const target = index + dir
      if (!this.draft || target < 0 || target >= this.draft.steps.length) return
      const steps = this.draft.steps
      const row = steps.splice(index, 1)[0]
      steps.splice(target, 0, row)
      this.repositionSteps()
    },
    repositionSteps () {
      const steps = this.draft.steps || []
      steps.forEach((step, index) => {
        step.position = index + 1
      })
    },
    toggleStep (step) {
      this.$set(this.expandedSteps, step.localId, !this.expandedSteps[step.localId])
    },
    jobForStep (step) {
      return (step && step.job) || this.jobs.find((job) => job.key === step.jobKey) || {}
    },
    jobName (key) {
      const job = this.jobs.find((row) => row.key === key)
      return (job && job.name) || key || '-'
    },
    jobOptionsForStep (step) {
      const job = this.jobForStep(step)
      return Array.isArray(job.options) ? job.options : []
    },
    applyOptionDefaults (step) {
      const out = Object.assign({}, step.options || {})
      this.jobOptionsForStep(step).forEach((opt) => {
        if (out[opt.key] === undefined) out[opt.key] = opt.default !== undefined ? opt.default : this.defaultForOption(opt)
      })
      return out
    },
    defaultForOption (opt) {
      if (!opt) return ''
      if (opt.type === 'boolean') return false
      if (opt.type === 'number') return 0
      if (opt.type === 'select' && Array.isArray(opt.choices) && opt.choices.length) return this.optionChoices(opt)[0].value
      return ''
    },
    optionChoices (opt) {
      return (Array.isArray(opt.choices) ? opt.choices : []).map((choice) => {
        if (choice && typeof choice === 'object') {
          return {
            value: choice.value !== undefined ? choice.value : choice.key,
            label: choice.label || choice.name || choice.value || choice.key
          }
        }
        return { value: choice, label: choice }
      })
    },
    optionLabel (opt) {
      const key = String((opt && opt.key) || '')
      const labels = {
        mode: 'Mode',
        maxDirectives: 'Max directives',
        runPipeline: 'Run full candidate pipeline',
        minCount: 'Min candidates per target',
        scope: 'Parts scope',
        delayMs: 'Delay between external calls',
        limit: 'Limit',
        dryRun: 'Dry run',
        rescanAfterFix: 'Rescan after fix',
        rescanAfterRepair: 'Rescan after repair'
      }
      return labels[key] || this.prettyLabel((opt && opt.label) || key)
    },
    optionSummary (step) {
      const opts = this.jobOptionsForStep(step)
      const parts = []
      opts.forEach((opt) => {
        const value = (step.options || {})[opt.key]
        const def = opt.default !== undefined ? opt.default : this.defaultForOption(opt)
        if (String(value) !== String(def)) parts.push(`${opt.key}: ${this.displayValue(value)}`)
      })
      return parts.slice(0, 3).join(' | ')
    },
    previousStepPositions (index) {
      const out = []
      for (let i = 1; i <= index; i++) out.push(i)
      return out
    },
    runIfSource (step) {
      return (step.runIf && step.runIf.source) || ''
    },
    setRunIfSource (step, source) {
      if (!source) {
        this.clearRunIf(step)
        return
      }
      const next = Object.assign({ source, key: source === 'context' ? 'candidateIds' : 'findings', op: 'nonEmpty' }, step.runIf || {})
      next.source = source
      if (source === 'step' && !next.from) next.from = 1
      if (source === 'context') this.$delete(next, 'from')
      this.$set(step, 'runIf', next)
    },
    clearRunIf (step) {
      this.$set(step, 'runIf', null)
    },
    runIfSummary (step) {
      const r = step && step.runIf
      if (!r || !r.source) return 'Always run this step.'
      const key = r.key || 'value'
      const op = r.op === 'gt' ? 'is greater than' : (r.op === 'eq' ? 'equals' : 'is non-empty')
      if (r.source === 'step') {
        return `Only run if step ${r.from || 1}'s ${key} ${op}${r.op === 'nonEmpty' ? '' : ' ' + this.displayValue(r.value)}.`
      }
      return `Only run if context ${key} ${op}${r.op === 'nonEmpty' ? '' : ' ' + this.displayValue(r.value)}.`
    },
    async runPipeline (pipeline) {
      if (!pipeline || this.pipelineIsRunning(pipeline)) return
      try {
        const { data } = await axios.post(`${apiDomain}pipelines/${encodeURIComponent(pipeline.key)}/run`, { options: {} }, {
          headers: this.authHeaders()
        })
        const run = data && data.run
        if (data && data.started === false) {
          this.notice = data.reason === 'already-running' ? 'Pipeline is already running.' : (data.reason || 'Pipeline did not start.')
        } else {
          this.notice = `Pipeline run #${run && run.id ? run.id : ''} started.`
        }
        if (run && run.id) {
          this.mergePipelineRun(run)
          this.viewRun(run)
        }
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    async viewRun (run) {
      if (!run || !run.id) return
      this.runLoading = true
      // Subscribe before the detail request so orchestration log lines emitted
      // during the HTTP round trip are not missed.
      this.selectedRun = this.normalizeRunDetail(Object.assign({}, run))
      this.ensureRunPolling()
      try {
        const { data } = await axios.get(`${apiDomain}pipelines/runs/${encodeURIComponent(run.id)}`, {
          headers: this.authHeaders()
        })
        this.selectedRun = this.mergeSelectedRunSnapshot(data && data.run)
        this.$router.replace({ path: this.$route.path, query: Object.assign({}, this.$route.query, { run: String(run.id) }) }).catch(() => {})
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.runLoading = false
      }
    },
    normalizeRunDetail (run) {
      const row = Object.assign({}, run || {})
      row.status = String(row.status || '').toLowerCase()
      row.steps = Array.isArray(row.steps) ? row.steps : ((row.stats && row.stats.steps) || [])
      row.changesCount = this.runChangeCount(row)
      return row
    },
    closeRun () {
      this.selectedRun = null
      if (this.unsubscribeRunLive) this.unsubscribeRunLive()
      this.unsubscribeRunLive = null
      const query = Object.assign({}, this.$route.query)
      delete query.run
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    ensureRunPolling () {
      this.subscribeRunLive()
    },
    async cancelPipelineRun (run) {
      if (!run || !run.id) return
      try {
        const { data } = await axios.post(`${apiDomain}pipelines/runs/${encodeURIComponent(run.id)}/cancel`, {}, {
          headers: this.authHeaders()
        })
        this.notice = `Cancel requested. Status: ${(data && data.status) || 'cancelled'}.`
        await this.viewRun(run)
        await this.fetchAll(false)
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    isActiveRun (run) {
      return ACTIVE_STATUSES.indexOf(String((run && run.status) || '').toLowerCase()) >= 0
    },
    isExecutingStep (step) {
      return ['running', 'starting', 'active'].indexOf(String((step && step.status) || '').toLowerCase()) >= 0
    },
    pipelineIsRunning (pipeline) {
      const last = pipeline && pipeline.lastRun
      return this.isActiveRun(last)
    },
    pipelineStatus (pipeline) {
      if (this.pipelineIsRunning(pipeline)) return String(pipeline.lastRun.status).toLowerCase()
      return String((pipeline && pipeline.lastStatus) || 'none').toLowerCase()
    },
    statusLabel (status) {
      const key = String(status || 'none').toLowerCase()
      const map = {
        none: 'Not run',
        running: 'Running',
        starting: 'Starting',
        queued: 'Queued',
        pending: 'Pending',
        success: 'Success',
        partial: 'Partial',
        failed: 'Failed',
        cancelled: 'Cancelled',
        canceled: 'Cancelled',
        skipped: 'Skipped'
      }
      return map[key] || this.prettyLabel(key)
    },
    stepCount (pipeline) {
      return ((pipeline && pipeline.steps) || []).length
    },
    runSteps (run) {
      const actual = Array.isArray(run && run.steps) ? run.steps : []
      const pipelineKey = (run && (run.pipelineKey || run.pipeline_key)) || this.selectedKey
      const pipeline = (this.selectedPipeline && this.selectedPipeline.key === pipelineKey)
        ? this.selectedPipeline
        : (this.pipelines || []).find((item) => item.key === pipelineKey)
      const configured = ((pipeline && pipeline.steps) || [])
        .filter((step) => step.enabled !== false)

      if (!configured.length) return actual

      const matched = {}
      const merged = configured.map((step, index) => {
        const foundIndex = actual.findIndex((candidate, candidateIndex) => {
          if (matched[candidateIndex]) return false
          if (candidate.position !== undefined && candidate.position !== null) {
            return Number(candidate.position) === index + 1
          }
          return candidate.jobKey && candidate.jobKey === step.jobKey
        })
        if (foundIndex >= 0) matched[foundIndex] = true
        return Object.assign({
          position: index + 1,
          jobKey: step.jobKey,
          jobName: this.jobName(step.jobKey),
          label: step.label || this.jobName(step.jobKey),
          status: 'pending'
        }, step, foundIndex >= 0 ? actual[foundIndex] : {})
      })

      actual.forEach((step, index) => {
        if (!matched[index]) merged.push(step)
      })
      return merged
    },
    completedStepCount (run) {
      const completedStatuses = ['success', 'succeeded', 'completed', 'complete']
      const fromSteps = this.runSteps(run).filter((step) => {
        return completedStatuses.indexOf(String((step && step.status) || '').toLowerCase()) >= 0
      }).length
      return Math.max(fromSteps, Number(run && run.stepsSucceeded) || 0)
    },
    runStepTotal (run) {
      return Number(run && run.stepsTotal) || this.runSteps(run).length
    },
    currentStepNumber (run) {
      const active = this.runSteps(run).find((step) => this.isExecutingStep(step))
      if (active && Number(active.position)) return Number(active.position)
      const total = this.runStepTotal(run)
      return total ? Math.min(this.completedStepCount(run) + 1, total) : 0
    },
    runProgressLabel (run) {
      const completed = this.completedStepCount(run)
      const total = this.runStepTotal(run)
      if (!total) return 'Waiting for steps'
      if (this.isActiveRun(run)) {
        return `Step ${this.currentStepNumber(run)} of ${total} | ${completed} completed`
      }
      return `${completed} of ${total} completed`
    },
    activeStepTitle (run) {
      const step = this.runSteps(run).find((item) => this.isExecutingStep(item))
      if (!step) return 'Preparing the next pipeline step'
      return step.label || step.jobName || step.jobKey || 'Pipeline step running'
    },
    runUpdatedLabel () {
      if (!this.runLastUpdatedAt) return 'just now'
      const seconds = Math.max(0, Math.floor((this.clockNow - this.runLastUpdatedAt) / 1000))
      if (seconds < 2) return 'just now'
      if (seconds < 60) return `${seconds}s ago`
      return `${Math.floor(seconds / 60)}m ago`
    },
    stepScopeLabel (step) {
      if (!step || typeof step !== 'object') return ''
      const childRun = step.run && typeof step.run === 'object'
        ? step.run
        : (step.jobRun && typeof step.jobRun === 'object' ? step.jobRun : {})
      const stats = step.stats && typeof step.stats === 'object' ? step.stats : {}
      const scope = step.scopeSnapshot || step.scope_snapshot || step.scope ||
        childRun.scopeSnapshot || childRun.scope_snapshot || childRun.scope ||
        stats.scopeSnapshot || stats.scope_snapshot || stats.scope

      if (scope && typeof scope === 'object') {
        const countKeys = ['total', 'eligible', 'selected', 'count', 'previewCount', 'preview_count']
        let count = null
        for (let i = 0; i < countKeys.length; i++) {
          const value = scope[countKeys[i]]
          if (value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
            count = Number(value)
            break
          }
        }
        if (count === null && Array.isArray(scope.items)) count = scope.items.length
        const descriptor = scope.descriptor && typeof scope.descriptor === 'object' ? scope.descriptor : {}
        const label = scope.label || scope.name || scope.mode || scope.type || descriptor.label || descriptor.name || descriptor.mode
        if (count !== null) {
          const rows = `${count} ${count === 1 ? 'row' : 'rows'}`
          return label ? `${rows} (${this.prettyLabel(label)})` : rows
        }
        if (label) return this.prettyLabel(label)
      } else if (scope !== undefined && scope !== null && scope !== '') {
        return this.prettyLabel(scope)
      }

      const configuredSteps = (this.draft && Array.isArray(this.draft.steps) && this.draft.steps.length)
        ? this.draft.steps
        : ((this.selectedPipeline && this.selectedPipeline.steps) || [])
      const configuredStep = configuredSteps.find((item, index) => {
        if (step.position !== undefined && step.position !== null) return Number(step.position) === index + 1
        return step.jobKey && item.jobKey === step.jobKey
      }) || {}
      const options = step.options || (step.input && step.input.options) || childRun.options || configuredStep.options || {}
      if (options.scope !== undefined && options.scope !== null && options.scope !== '') {
        return this.prettyLabel(options.scope)
      }
      return ''
    },
    stepChangeCount (step) {
      if (!step || typeof step !== 'object') return 0
      if (step.changes && typeof step.changes === 'object') {
        const total = Number(step.changes.total)
        return Number.isFinite(total) ? total : 0
      }
      const count = Number(step.changesCount !== undefined ? step.changesCount : step.changes)
      return Number.isFinite(count) ? count : 0
    },
    headlineStats (stats) {
      if (!stats || typeof stats !== 'object') return []
      const keys = ['processed', 'total', 'findings', 'fixed', 'changes', 'created', 'enriched', 'failed', 'skipped']
      const out = []
      keys.forEach((key) => {
        const value = stats[key]
        if (value !== undefined && value !== null && value !== '') out.push({ key, label: this.prettyLabel(key), value })
      })
      return out.slice(0, 5)
    },
    changeBag (run) {
      return run && run.changes && typeof run.changes === 'object' ? run.changes : {}
    },
    runChangeCount (run) {
      if (!run || typeof run !== 'object') return 0
      const bag = this.changeBag(run)
      const candidates = [bag.total, run.changesCount, typeof run.changes !== 'object' ? run.changes : null]
      for (let i = 0; i < candidates.length; i++) {
        if (candidates[i] === undefined || candidates[i] === null || candidates[i] === '') continue
        const value = Number(candidates[i])
        if (Number.isFinite(value)) return value
      }
      return 0
    },
    changeTotal (run) {
      return this.runChangeCount(run)
    },
    changeSummary (run) {
      const total = this.runChangeCount(run)
      if (total > 0) return `${total} catalog ${total === 1 ? 'field' : 'fields'}`
      return this.isActiveRun(run) ? 'No field changes yet' : 'No field changes'
    },
    changeRows (run, key) {
      const rows = this.changeBag(run)[key]
      return Array.isArray(rows) ? rows : []
    },
    changeSample (run) {
      const rows = this.changeBag(run).sample
      return Array.isArray(rows) ? rows : []
    },
    barWidth (value, rows) {
      const max = Math.max.apply(null, (rows || []).map((row) => Number(row.count) || 0).concat([1]))
      return Math.max(4, Math.round((Number(value) || 0) / max * 100)) + '%'
    },
    duration (row) {
      const ms = Number(row && row.durationMs)
      if (Number.isFinite(ms) && ms > 0) return this.formatDuration(ms)
      const start = row && row.startedAt ? new Date(row.startedAt).getTime() : 0
      const end = row && row.finishedAt ? new Date(row.finishedAt).getTime() : 0
      if (start && end && end >= start) return this.formatDuration(end - start)
      return '-'
    },
    liveDuration (row) {
      if (!this.isActiveRun(row)) return this.duration(row)
      const start = row && row.startedAt ? new Date(row.startedAt).getTime() : 0
      if (!start || isNaN(start)) return this.duration(row)
      return this.formatDuration(Math.max(0, this.clockNow - start))
    },
    formatDuration (ms) {
      const sec = Math.round(ms / 1000)
      if (sec < 60) return `${sec}s`
      const min = Math.floor(sec / 60)
      const rem = sec % 60
      if (min < 60) return `${min}m ${rem}s`
      const h = Math.floor(min / 60)
      return `${h}h ${min % 60}m`
    },
    fmtTime (t) {
      if (!t) return '-'
      const d = new Date(t)
      return isNaN(d) ? '-' : d.toLocaleString()
    },
    fmtRelative (t) {
      if (!t) return '-'
      const d = new Date(t)
      if (isNaN(d)) return '-'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return d.toLocaleDateString()
    },
    cronHint (cron) {
      if (!cron) return 'Manual only'
      const map = {
        '0 3 * * *': 'Daily 03:00',
        '0 7 * * *': 'Daily 07:00',
        '0 4 * * 1': 'Weekly Mon 04:00'
      }
      return map[cron] || cron
    },
    displayValue (value) {
      if (value === undefined || value === null || value === '') return '-'
      if (typeof value === 'object') return JSON.stringify(value)
      const text = String(value)
      return text.length > 120 ? text.slice(0, 117) + '...' : text
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase())
    },
    errMsg (e) {
      return (e && e.response && e.response.data && (e.response.data.message || (e.response.data.error && e.response.data.error.message))) ||
        (e && e.message) || 'Request failed'
    }
  }
}
</script>

<style scoped>
.pipelines-page {
  padding: 4px 8px 44px;
  color: #cfd2da;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow,
.section-title,
.mini-title {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 860px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.alert,
.notice,
.warning-row {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.45;
}

.alert,
.warning-row.danger {
  border: 1px solid rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.42);
  color: #f3c6cf;
}

.notice {
  border: 1px solid rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #a6ead2;
}

.warning-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid rgba(224, 192, 126, 0.42);
  background: rgba(77, 58, 31, 0.16);
  color: #e0c07e;
}

.warning-row a {
  color: #9bdff5;
  text-decoration: none;
  white-space: nowrap;
}

.pipeline-workspace,
.step-list {
  display: grid;
  gap: 12px;
}

.pipeline-index-panel,
.empty-panel {
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 8px;
  background:
    radial-gradient(circle at 10% 0%, rgba(25, 151, 198, 0.06), transparent 38%),
    rgba(8, 11, 17, 0.62);
}

.editor-head,
.editor-actions,
.section-title-row,
.step-row-main,
.timeline-title,
.panel-head,
.panel-head-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.editor-head h5,
.panel-head h5 {
  margin: 0;
  color: #dce1eb;
  font-size: 17px;
  font-weight: 800;
}

.editor-head p,
.section-title-row p,
.timeline-main small {
  margin: 0;
  color: #8f98aa;
  font-size: 12.5px;
  line-height: 1.45;
}

.muted-line,
.muted-sub {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 700;
}

.pipeline-index-panel {
  padding: 14px;
}

.pipeline-index-table {
  margin-top: 12px;
}

.pipeline-index-table >>> .gc-admin-table {
  background: rgba(8, 11, 17, 0.42);
}

.pipeline-index-table >>> .gc-table-row.is-selected {
  background: rgba(25, 151, 198, 0.1);
  box-shadow: inset 3px 0 0 rgba(25, 151, 198, 0.8);
}

.pipeline-index-table >>> .gc-table-row.is-running {
  box-shadow: inset 3px 0 0 rgba(27, 201, 142, 0.7);
}

.pipeline-index-table >>> .gc-table-row.has-issues {
  box-shadow: inset 3px 0 0 rgba(232, 79, 106, 0.68);
}

.pipeline-name-cell {
  display: grid;
  gap: 4px;
}

.pipeline-name-cell strong {
  color: #e4e8f0;
  font-size: 13px;
}

.pipeline-name-cell small {
  color: #6f788a;
  font-size: 11px;
  font-weight: 800;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  gap: 7px;
}

.pipeline-editor-modal {
  position: fixed;
  inset: 0;
  z-index: 1180;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.64);
}

.pipeline-main {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(380px, 500px) minmax(0, 1fr);
  align-items: start;
  width: min(1180px, calc(100vw - 56px));
  max-height: calc(100vh - 56px);
  overflow: auto;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    #080b11;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.48);
}

.editor-panel,
.steps-panel,
.history-panel {
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.52);
}

.editor-header-panel {
  grid-column: 1 / -1;
  padding: 0;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(10, 14, 21, 0.98), rgba(8, 11, 17, 0.96));
}

.editor-settings-panel {
  grid-column: 1;
  max-height: calc(100vh - 190px);
  overflow: auto;
}

.pipeline-workflow-column {
  grid-column: 2;
  display: grid;
  align-self: start;
  gap: 14px;
  min-width: 0;
}

.steps-panel {
  align-self: start;
}

.editor-head {
  align-items: flex-start;
  margin: -14px -14px 0;
  padding: 14px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.54);
  background:
    linear-gradient(180deg, rgba(10, 14, 21, 0.98), rgba(8, 11, 17, 0.94));
}

.editor-header-panel .editor-head {
  margin: 0;
  border-bottom: 0;
}

.editor-title-block {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.editor-summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 6px;
}

.editor-summary-row span {
  padding: 3px 8px;
  border: 1px solid rgba(67, 72, 87, 0.68);
  border-radius: 999px;
  background: rgba(8, 11, 17, 0.62);
  color: #aeb6c8;
  font-size: 11px;
  font-weight: 800;
}

.editor-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.settings-section-head {
  display: grid;
  gap: 2px;
  margin-top: 14px;
}

.settings-section-head p {
  margin: 0;
  color: #8f98aa;
  font-size: 12.5px;
}

.pipeline-settings,
.option-form,
.runif-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.editor-settings-panel .pipeline-settings {
  grid-template-columns: 1fr;
}

.editor-settings-panel .field-wide {
  grid-column: auto;
}

.field {
  display: grid;
  gap: 5px;
  margin: 0;
}

.field-wide {
  grid-column: span 2;
}

.field span,
.opt-row > label {
  color: #7f889a;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field input,
.field select,
.field textarea,
.opt-row input,
.opt-row select,
.opt-row textarea,
.config-row input,
.add-step select {
  min-height: 34px;
  border: 1px solid #434857;
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.74);
  color: #d6dbe5;
  padding: 0 10px;
  outline: none;
}

.description-input {
  min-height: 96px;
  line-height: 1.45;
}

.field textarea,
.opt-row textarea {
  padding: 9px 10px;
  resize: vertical;
}

.field input:focus,
.field select:focus,
.field textarea:focus,
.opt-row input:focus,
.opt-row select:focus,
.opt-row textarea:focus,
.config-row input:focus,
.add-step select:focus {
  border-color: #1997c6;
}

.field input:disabled {
  opacity: 0.68;
}

.setting-switch,
.mini-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #aeb6c8;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.setting-switch {
  align-self: end;
  min-height: 34px;
}

.mini-switch.inline {
  justify-content: flex-start;
}

.setting-switch input,
.mini-switch input {
  display: none;
}

.switch-track {
  position: relative;
  width: 34px;
  height: 18px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 999px;
  background: rgba(8, 11, 17, 0.9);
}

.switch-track:after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6f788a;
  transition: transform 0.16s ease, background 0.16s ease;
}

input:checked + .switch-track {
  border-color: rgba(27, 201, 142, 0.52);
  background: rgba(27, 201, 142, 0.13);
}

input:checked + .switch-track:after {
  transform: translateX(16px);
  background: #1bc98e;
}

.preset-row,
.add-step,
.step-actions,
.stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.add-step {
  flex: 0 0 min(420px, 100%);
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.add-step select {
  flex: 1 1 auto;
  min-width: 0;
}

.preset-row {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid rgba(67, 72, 87, 0.44);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.28);
}

.preset-row > span {
  flex: 0 0 100%;
  color: #7f889a;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.preset-btn {
  flex: 1 1 132px;
}

.preset-btn,
.btn-ghost,
.btn-refresh,
.btn-run,
.btn-save,
.btn-danger-subtle,
.icon-btn {
  min-height: 34px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.45);
  color: #aeb6c8;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 12px;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.btn-refresh,
.btn-save,
.btn-run {
  border-color: rgba(25, 151, 198, 0.58);
  background: rgba(25, 151, 198, 0.1);
  color: #9bdff5;
}

.btn-save {
  background: #16a9d6;
  border-color: #16a9d6;
  color: #071018;
}

.pipeline-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.live-connection {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 999px;
  padding: 7px 10px;
  color: #aab5c7;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.live-connection i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f5c84c;
  box-shadow: 0 0 8px rgba(245, 200, 76, 0.45);
}

.live-connection.online {
  color: #7ef0c9;
  border-color: rgba(20, 184, 166, 0.32);
}

.live-connection.online i {
  background: #18d59f;
  box-shadow: 0 0 10px rgba(24, 213, 159, 0.55);
}

.btn-danger-subtle,
.icon-btn.danger {
  border-color: rgba(232, 79, 106, 0.5);
  color: #f3a3b1;
  background: rgba(90, 34, 48, 0.18);
}

.btn-ghost:hover,
.btn-refresh:hover,
.btn-run:hover,
.btn-save:hover,
.preset-btn:hover,
.icon-btn:hover {
  border-color: #1997c6;
  color: #eef8fc;
}

button:disabled {
  opacity: 0.45;
  cursor: default;
}

.compact,
.tiny {
  min-height: 30px;
  padding: 4px 10px;
}

.dirty-pill,
.stage-chip,
.missing-chip,
.conflict-chip,
.option-chip,
.status-pill,
.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.dirty-pill {
  padding: 5px 9px;
  border: 1px solid rgba(224, 192, 126, 0.42);
  color: #e0c07e;
  background: rgba(77, 58, 31, 0.15);
}

.status-pill {
  padding: 5px 9px;
  border: 1px solid rgba(117, 124, 146, 0.32);
  color: #aeb6c8;
  background: rgba(117, 124, 146, 0.1);
  text-transform: uppercase;
}

.status-pill.st-running,
.status-pill.st-starting,
.status-pill.st-queued,
.status-pill.st-pending {
  border-color: rgba(25, 151, 198, 0.5);
  color: #9bdff5;
  background: rgba(25, 151, 198, 0.12);
}

.status-pill.st-success {
  border-color: rgba(27, 201, 142, 0.48);
  color: #89dfbd;
  background: rgba(27, 201, 142, 0.12);
}

.status-pill.st-partial,
.status-pill.st-skipped {
  border-color: rgba(224, 192, 126, 0.52);
  color: #e0c07e;
  background: rgba(77, 58, 31, 0.18);
}

.status-pill.st-failed,
.status-pill.st-cancelled,
.status-pill.st-canceled {
  border-color: rgba(232, 79, 106, 0.5);
  color: #f3a3b1;
  background: rgba(90, 34, 48, 0.2);
}

.config-panel,
.runif-panel {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.36);
}

.config-grid {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.config-row {
  display: grid;
  grid-template-columns: minmax(120px, 0.8fr) minmax(120px, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.icon-btn {
  width: 34px;
  padding: 0;
}

.step-row {
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background:
    linear-gradient(90deg, rgba(25, 151, 198, 0.045), transparent 34%),
    rgba(8, 11, 17, 0.54);
}

.step-row.disabled {
  opacity: 0.68;
}

.step-row.missing {
  border-color: rgba(232, 79, 106, 0.52);
}

.step-row > .warning-row {
  margin: 0 12px 12px 54px;
  padding: 9px 11px;
  font-size: 12px;
}

.step-row-main {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 13px;
}

.step-pos,
.timeline-pos {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(25, 151, 198, 0.5);
  border-radius: 8px;
  color: #9bdff5;
  font-weight: 900;
}

.step-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.step-title-line {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
}

.step-title-line strong {
  color: #e4e8f0;
}

.step-copy small {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 3px;
  color: #8f98aa;
}

.step-copy b {
  color: #657085;
  font-weight: 800;
}

.step-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.move-actions {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border: 1px solid rgba(67, 72, 87, 0.55);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.4);
}

.move-actions .icon-btn {
  width: 28px;
  min-height: 28px;
  border-color: transparent;
  background: transparent;
}

.step-edit-btn {
  min-width: 52px;
}

.stage-chip,
.option-chip {
  padding: 3px 7px;
  border: 1px solid rgba(117, 124, 146, 0.32);
  color: #aeb6c8;
  background: rgba(117, 124, 146, 0.1);
}

.missing-chip {
  padding: 3px 7px;
  border: 1px solid rgba(232, 79, 106, 0.45);
  color: #f3a3b1;
  background: rgba(90, 34, 48, 0.18);
}

.conflict-chip {
  padding: 3px 7px;
  border: 1px solid rgba(224, 192, 126, 0.45);
  color: #e0c07e;
  background: rgba(77, 58, 31, 0.16);
}

.step-editor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  max-width: 100%;
  margin: 0 12px 12px 54px;
  padding: 10px;
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.36);
}

.step-editor > .step-editor-section,
.step-editor > .runif-panel,
.step-editor > .muted-line {
  grid-column: 1 / -1;
}

.step-editor-section {
  display: grid;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(67, 72, 87, 0.48);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.28);
}

.step-editor-section-main {
  grid-template-columns: minmax(0, 1fr) minmax(180px, 0.55fr);
  align-items: end;
}

.step-editor-section-main .step-editor-title {
  grid-column: 1 / -1;
}

.step-editor-title {
  color: #1997c6;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.opt-row {
  min-width: 0;
  display: grid;
  gap: 5px;
}

.option-form {
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-top: 0;
}

.runif-panel .runif-grid {
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
}

.runif-panel {
  margin-top: 0;
}

.opt-row.wide {
  grid-column: span 2;
}

.runif-grid {
  margin-top: 10px;
}

.section-title-row.compact {
  align-items: flex-start;
}

.history-panel {
  margin-bottom: 32px;
}

.pipeline-history-table {
  margin-top: 12px;
}

.pipeline-history-table >>> .gc-admin-table,
.changes-table >>> .gc-admin-table {
  background: rgba(8, 11, 17, 0.4);
}

.pipeline-history-table >>> .gc-table-row:hover {
  background: rgba(25, 151, 198, 0.08);
}

.empty-panel {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 44px 18px;
}

.empty-panel .icon {
  color: #1997c6;
  font-size: 26px;
}

.run-drawer {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.58);
}

.run-panel {
  width: min(1120px, calc(100vw - 48px));
  max-width: 100vw;
  min-width: 0;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 18px;
  border-left: 1px solid rgba(67, 72, 87, 0.9);
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.07), transparent 38%),
    #080b11;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.42);
}

.panel-head {
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.72);
}

.run-active-progress {
  position: relative;
  height: 3px;
  margin: -1px 0 0;
  overflow: hidden;
  background: rgba(25, 151, 198, 0.12);
}

.run-active-progress span {
  position: absolute;
  inset-block: 0;
  width: 34%;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, #20b9ec, #43d7c0, transparent);
  box-shadow: 0 0 14px rgba(32, 185, 236, 0.42);
  animation: pipeline-progress 1.55s ease-in-out infinite;
}

.run-live-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin: 14px 0 0;
  padding: 10px 12px;
  border: 1px solid rgba(25, 151, 198, 0.34);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(25, 151, 198, 0.11), rgba(8, 11, 17, 0.42));
}

.run-live-copy {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.run-live-copy > div,
.run-heartbeat {
  display: grid;
  gap: 2px;
}

.run-live-copy strong {
  overflow: hidden;
  color: #e4e8f0;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.run-live-copy span,
.run-heartbeat small {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 700;
}

.run-live-dot,
.run-log-live i,
.changes-collecting i {
  display: inline-block;
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22d3a2;
  box-shadow: 0 0 0 0 rgba(34, 211, 162, 0.45);
  animation: pipeline-pulse 1.65s ease-out infinite;
}

.run-heartbeat {
  flex: 0 0 auto;
  justify-items: end;
}

.run-heartbeat > span {
  color: #75e8c5;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(67, 72, 87, 0.8);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.5);
  color: #aeb6c8;
  cursor: pointer;
  font-size: 18px;
}

.run-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 10px;
  margin: 14px 0;
}

.run-summary-grid > div,
.breakdown-card {
  display: grid;
  min-width: 0;
  gap: 5px;
  padding: 11px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.5);
}

.run-summary-grid span {
  color: #7f889a;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.run-summary-grid strong {
  min-width: 0;
  overflow-wrap: anywhere;
  color: #e4e8f0;
  font-size: 14px;
}

.run-section {
  display: grid;
  width: 100%;
  min-width: 0;
  gap: 12px;
  margin-top: 18px;
}

.timeline {
  display: grid;
  min-width: 0;
  gap: 8px;
}

.timeline-step {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.5);
}

.timeline-step.st-running {
  border-color: rgba(25, 151, 198, 0.52);
  background:
    linear-gradient(90deg, rgba(25, 151, 198, 0.1), transparent 48%),
    rgba(8, 11, 17, 0.5);
  box-shadow: inset 3px 0 0 rgba(32, 185, 236, 0.72);
}

.timeline-step.st-success {
  border-color: rgba(27, 201, 142, 0.36);
}

.timeline-step.st-failed {
  border-color: rgba(232, 79, 106, 0.5);
}

.timeline-step.st-skipped {
  opacity: 0.82;
}

.timeline-step.st-pending {
  opacity: 0.62;
}

.timeline-main {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.timeline-meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 5px 14px;
  color: #8f98aa;
  font-size: 12px;
  line-height: 1.4;
}

.timeline-meta span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.timeline-meta b {
  margin-right: 5px;
  color: #687286;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.timeline-actions {
  display: flex;
  align-items: center;
  align-self: start;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
}

.job-run-link {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(25, 151, 198, 0.5);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.08);
  color: #16b4ed;
  font-size: 11px;
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;
}

.job-run-link:hover,
.job-run-link:focus {
  border-color: rgba(25, 151, 198, 0.82);
  background: rgba(25, 151, 198, 0.16);
  color: #9bdff5;
  text-decoration: none;
}

.skip-reason,
.step-error {
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
}

.skip-reason {
  border: 1px solid rgba(117, 124, 146, 0.32);
  color: #aeb6c8;
  background: rgba(117, 124, 146, 0.08);
}

.step-error {
  border: 1px solid rgba(232, 79, 106, 0.42);
  color: #f3a3b1;
  background: rgba(90, 34, 48, 0.16);
}

.stat-chip {
  padding: 4px 8px;
  border: 1px solid rgba(25, 151, 198, 0.28);
  color: #dce1eb;
  background: rgba(25, 151, 198, 0.08);
}

.stat-chip b {
  margin-right: 5px;
  color: #8f98aa;
}

.change-total {
  color: #89dfbd;
}

.changes-collecting,
.run-log-live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #75e8c5;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.run-results-waiting {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 92px;
  padding: 16px;
  border: 1px dashed rgba(67, 72, 87, 0.8);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.34);
}

.run-results-waiting__icon {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  padding: 9px;
  border: 1px solid rgba(25, 151, 198, 0.4);
  border-radius: 8px;
  background: rgba(25, 151, 198, 0.09);
}

.run-results-waiting__icon span {
  width: 4px;
  border-radius: 4px;
  background: #24b7e7;
  animation: pipeline-bars 1.1s ease-in-out infinite;
}

.run-results-waiting__icon span:nth-child(1) { height: 8px; }
.run-results-waiting__icon span:nth-child(2) { height: 16px; animation-delay: 0.14s; }
.run-results-waiting__icon span:nth-child(3) { height: 11px; animation-delay: 0.28s; }

.run-results-waiting strong {
  color: #dfe5ed;
  font-size: 13px;
}

.run-results-waiting p {
  margin: 4px 0 0;
  color: #8f98aa;
  font-size: 12px;
  line-height: 1.45;
}

.change-breakdowns {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.bar-list {
  display: grid;
  gap: 8px;
}

.bar-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding-bottom: 7px;
  color: #cfd6e3;
  font-size: 12px;
}

.bar-row span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-row b {
  color: #e4e8f0;
}

.bar-row em {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  border-radius: 999px;
  background: #16b4ed;
}

.changes-table {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin-top: 12px;
}

.changes-table >>> .gc-table-shell,
.changes-table >>> .gc-table-scroll {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.changes-table small {
  display: block;
  margin-top: 3px;
  color: #8f98aa;
}

.field-code {
  color: #9bdff5;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}

.truncate-value {
  display: block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.run-log {
  min-width: 0;
  margin: 18px 0 28px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.5);
}

.run-log summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  padding: 12px;
  color: #9bdff5;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.status-pill.is-live-status {
  position: relative;
  padding-left: 20px;
}

.status-pill.is-live-status::before {
  content: '';
  position: absolute;
  left: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #31c8f4;
  box-shadow: 0 0 0 0 rgba(49, 200, 244, 0.5);
  animation: pipeline-pulse 1.65s ease-out infinite;
}

@keyframes pipeline-progress {
  from { transform: translateX(-105%); }
  to { transform: translateX(300%); }
}

@keyframes pipeline-pulse {
  0% { box-shadow: 0 0 0 0 rgba(49, 200, 244, 0.5); }
  70% { box-shadow: 0 0 0 6px rgba(49, 200, 244, 0); }
  100% { box-shadow: 0 0 0 0 rgba(49, 200, 244, 0); }
}

@keyframes pipeline-bars {
  0%, 100% { opacity: 0.42; transform: scaleY(0.65); }
  50% { opacity: 1; transform: scaleY(1); }
}

@media (prefers-reduced-motion: reduce) {
  .run-active-progress span,
  .run-live-dot,
  .run-log-live i,
  .changes-collecting i,
  .run-results-waiting__icon span,
  .status-pill.is-live-status::before {
    animation: none;
  }
}

.run-log pre {
  max-width: 100%;
  max-height: 320px;
  overflow: auto;
  margin: 0;
  padding: 12px;
  border-top: 1px solid rgba(67, 72, 87, 0.62);
  color: #cfd6e3;
  white-space: pre-wrap;
}

@media screen and (max-width: 1100px) {
  .pipeline-main {
    grid-template-columns: 1fr;
  }

  .editor-header-panel,
  .editor-settings-panel,
  .pipeline-workflow-column {
    grid-column: 1;
  }

  .editor-settings-panel {
    max-height: none;
  }
}

@media screen and (max-width: 720px) {
  .label,
  .editor-head,
  .section-title-row,
  .step-row-main,
  .timeline-step,
  .warning-row,
  .panel-head {
    display: grid;
    justify-content: stretch;
  }

  .pipeline-settings,
  .option-form,
  .runif-grid,
  .run-summary-grid,
  .change-breakdowns {
    grid-template-columns: 1fr;
  }

  .timeline-step {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .timeline-actions {
    grid-column: 2;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .field-wide,
  .opt-row.wide {
    grid-column: auto;
  }

  .step-editor {
    grid-template-columns: 1fr;
    margin-left: 12px;
  }

  .step-editor-section-main {
    grid-template-columns: 1fr;
  }

  .step-row > .warning-row {
    margin-left: 12px;
  }

  .step-row-main {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .step-controls {
    grid-column: 1 / -1;
    justify-content: space-between;
  }

  .pipeline-editor-modal {
    padding: 10px;
  }

  .pipeline-main {
    width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    grid-template-columns: 1fr;
    padding: 10px;
  }

  .editor-header-panel,
  .editor-settings-panel,
  .pipeline-workflow-column {
    grid-column: 1;
  }

  .run-panel {
    width: 100vw;
  }

  .run-live-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .run-heartbeat {
    justify-items: start;
  }
}
</style>
